import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import styles from "./RentPages.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { fetchProperties } from "../../redux/propertiesSlice";
import { RootState, AppDispatch } from "../../redux/store";
import { Property } from "../../types/property";
import {
  DEFAULT_FILTERS,
  default as Filter,
  PropertyFilters,
  applyPropertyFilters,
} from "./Filter";
import { ViewSwitcher } from "./PropertiesPage";
import { useSearchParams } from "react-router-dom";
import AreaGuideTeaser from "src/components/AreaGuide/AreaGuideTeaser";
import { TenantGuides } from "./RentPage";
import AboutAndContactForm from "src/components/AboutAndContactForm/AboutAndContactForm";
import { MAPBOX_TOKEN } from "../../config/mapbox";
import SafeImage from "../../components/SafeImage/SafeImage";

type PropertyRaw = Property;

// Extract lat/lng and image out of property/media fields
// Utility to get first image url, fallback to blank placeholder
const getFirstImageUrl = (property: PropertyRaw) => {
  if (
    property.media &&
    Array.isArray(property.media) &&
    property.media.length > 0
  ) {
    const img = property.media.find(
      (m: any) => m.media_type === 1 && m.media_url && m.file_type === "image"
    );
    return (
      img?.media_url ||
      property.media[0]?.media_url ||
      "https://via.placeholder.com/600x400"
    );
  }
  return "https://via.placeholder.com/600x400";
};

// Try to extract latitude/longitude from property.
// You may want to improve this to use geocoding or other logic if data incomplete.
const getLatLng = (property: PropertyRaw): [number, number] | null => {
  const lon = (property as any).longitude;
  const lat = (property as any).latitude;

  // Property may have .latitude / .longitude, or could need to fallback by postcode etc.
  if (typeof lat === "number" && typeof lon === "number") {
    return [lon, lat];
  }

  // Allow string numbers
  const parsedLat = typeof lat === "string" ? Number(lat) : null;
  const parsedLon = typeof lon === "string" ? Number(lon) : null;
  if (
    typeof parsedLat === "number" &&
    !Number.isNaN(parsedLat) &&
    typeof parsedLon === "number" &&
    !Number.isNaN(parsedLon)
  ) {
    return [parsedLon, parsedLat];
  }

  // fallback: cluster near default view to ensure visibility (deterministic per id)
  const base = defaultView.center;
  const seed = property.id ?? 1;
  const latOffset = ((seed % 7) - 3) * 0.0025;
  const lonOffset = (((seed * 5) % 7) - 3) * 0.0025;
  return [base[0] + lonOffset, base[1] + latOffset];
};

// Safely get a display address string
const getDisplayAddress = (property: PropertyRaw): string => {
  if ("display_address" in property) {
    return property.display_address as string;
  }
  // Compose from address fields if needed:
  const { address_1, address_2, address_3, address_4, postcode } =
    property as any;
  return [address_1, address_2, address_3, address_4, postcode]
    .filter(Boolean)
    .join(", ");
};

mapboxgl.accessToken = MAPBOX_TOKEN;

const defaultView = {
  center: [-0.1278, 51.5074],
  zoom: 12, // slightly more zoomed-in by default
};

const formatPrice = (price?: number) =>
  typeof price === "number" ? price.toLocaleString() : "";

const initialFilters: PropertyFilters = DEFAULT_FILTERS;

const MapViewPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    items: properties,
    status,
    error,
  } = useSelector((state: RootState) => state.properties);
  const [hoveredSelection, setHoveredSelection] = useState<PropertyRaw[]>([]);
  const [previewPos, setPreviewPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const previewRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [appliedFilters, setAppliedFilters] =
    useState<PropertyFilters>(initialFilters);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = useMemo(() => {
    const raw = searchParams.get("focusId");
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [searchParams]);
  // Refs for the map and markers
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<{ [key: string]: mapboxgl.Marker }>({});
  const focusedCardRef = useRef<HTMLDivElement | null>(null);

  // State for map loading
  const [isLoading, setIsLoading] = useState(true);

  // Map initialization
  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchProperties());
    }

    if (mapRef.current) return;

    const mapContainer = document.getElementById("map-view-map-container");
    if (!mapContainer) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainer,
      style: "mapbox://styles/mapbox/streets-v11",
      center: defaultView.center as [number, number],
      zoom: defaultView.zoom,
      attributionControl: false,
      antialias: true,
    });

    mapRef.current.on("load", () => {
      setIsLoading(false);
    });

    mapRef.current.on("error", (e) => {
      // eslint-disable-next-line no-console
      console.error("Mapbox error:", e);
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-left");
    mapRef.current.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
      }),
      "top-left"
    );

    return () => {
      // Only remove markers and destroy map instance if unmounting
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      Object.values(markersRef.current).forEach((marker) => {
        marker.remove();
      });
      markersRef.current = {};
    };
  }, [dispatch, status]);

  // hydrate filters from URL
  useEffect(() => {
    const params = searchParams;
    const num = (key: string): number | null => {
      const v = params.get(key);
      if (v === null || v === "") return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    };
    const bool = (key: string): boolean | null => {
      const v = params.get(key);
      if (v === null || v === "") return null;
      return v === "true";
    };
    const next: PropertyFilters = {
      propertyType: params.get("propertyType") || "",
      availableFrom: params.get("availableFrom") || "",
      minPrice: num("minPrice"),
      maxPrice: num("maxPrice"),
      bedrooms: num("bedrooms"),
      bathrooms: num("bathrooms"),
      isFurnished: bool("isFurnished"),
      newHome: bool("newHome"),
      status: params.get("status") || DEFAULT_FILTERS.status,
      minArea: num("minArea"),
      minDeposit: num("minDeposit"),
      maxDeposit: num("maxDeposit"),
      keyword: params.get("keyword") || "",
    };
    setAppliedFilters(next);
  }, [searchParams]);

  const updateSearchParams = (filters: PropertyFilters) => {
    const params = new URLSearchParams();
    if (filters.propertyType) params.set("propertyType", filters.propertyType);
    if (filters.availableFrom)
      params.set("availableFrom", filters.availableFrom);
    if (filters.minPrice !== null)
      params.set("minPrice", String(filters.minPrice));
    if (filters.maxPrice !== null)
      params.set("maxPrice", String(filters.maxPrice));
    if (filters.bedrooms !== null)
      params.set("bedrooms", String(filters.bedrooms));
    if (filters.bathrooms !== null)
      params.set("bathrooms", String(filters.bathrooms));
    if (filters.isFurnished !== null)
      params.set("isFurnished", String(filters.isFurnished));
    if (filters.newHome !== null)
      params.set("newHome", String(filters.newHome));
    if (filters.status && filters.status !== DEFAULT_FILTERS.status)
      params.set("status", filters.status);
    if (filters.minArea !== null)
      params.set("minArea", String(filters.minArea));
    if (filters.minDeposit !== null)
      params.set("minDeposit", String(filters.minDeposit));
    if (filters.maxDeposit !== null)
      params.set("maxDeposit", String(filters.maxDeposit));
    if (filters.keyword.trim()) params.set("keyword", filters.keyword.trim());
    setSearchParams(params);
  };

  const filtered = useMemo(() => {
    const base = applyPropertyFilters(properties, appliedFilters);
    if (focusId === null) return base;
    const focused = properties.find((p) => p.id === focusId);
    if (!focused) return base;
    if (base.some((p) => p.id === focusId)) return base;
    return [focused, ...base];
  }, [properties, appliedFilters, focusId]);

  const clearPreview = useCallback(() => {
    setHoveredSelection([]);
    setPreviewPos(null);
  }, []);

  const fitMapToResults = useCallback(
    (items: PropertyRaw[]) => {
      if (!mapRef.current) return;

      // Clear any hover preview before re-framing
      clearPreview();

      if (!items || items.length === 0) {
        mapRef.current.easeTo({
          center: defaultView.center as [number, number],
          zoom: defaultView.zoom,
          duration: 700,
        });
        return;
      }

      const coordsList = items
        .map(getLatLng)
        .filter(
          (c): c is [number, number] => Array.isArray(c) && c.length === 2
        );

      if (coordsList.length === 0) return;

      // If only one result, center on it.
      if (coordsList.length === 1) {
        mapRef.current.easeTo({
          center: coordsList[0],
          zoom: Math.max(defaultView.zoom, 14),
          duration: 700,
        });
        return;
      }

      const bounds = new mapboxgl.LngLatBounds();
      coordsList.forEach((c) => bounds.extend(c));

      const leftPadding = window.innerWidth >= 1024 ? 480 : 80; // account for sidebar on desktop
      mapRef.current.fitBounds(bounds, {
        padding: { top: 80, bottom: 80, left: leftPadding, right: 80 },
        duration: 800,
        maxZoom: 15,
      });
    },
    [clearPreview]
  );

  useEffect(() => {
    if (previewRef.current && previewPos && containerRef.current) {
      const el = previewRef.current;
      const rect = el.getBoundingClientRect();
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();

      const left = Math.min(
        Math.max(previewPos.x - rect.width / 2, 8),
        containerRect.width - rect.width - 8
      );
      const top = Math.max(previewPos.y - rect.height - 12, 8);

      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
    }
    if (!previewPos && previewRef.current) {
      previewRef.current.style.left = "";
      previewRef.current.style.top = "";
    }
  }, [previewPos]);

  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      if (
        previewRef.current &&
        !previewRef.current.contains(e.target as Node)
      ) {
        clearPreview();
      }
    };
    document.addEventListener("click", handleDocClick);
    return () => document.removeEventListener("click", handleDocClick);
  }, [clearPreview]);

  // Render markers on map
  const loadDataOnMap = useCallback(() => {
    if (!mapRef.current || status !== "succeeded") return;
    // Remove previous
    Object.values(markersRef.current).forEach((marker) => marker.remove());
    markersRef.current = {};

    const findNearby = (target: PropertyRaw) =>
      filtered.filter((p) => {
        const coords = getLatLng(p);
        const targetCoords = getLatLng(target);
        if (!coords || !targetCoords) return false;
        const [lon, lat] = coords;
        const [tLon, tLat] = targetCoords;
        const lonDiff = Math.abs(lon - tLon);
        const latDiff = Math.abs(lat - tLat);
        return lonDiff <= 0.001 && latDiff <= 0.001;
      });

    filtered.forEach((property) => {
      const coords = getLatLng(property);
      if (!coords) return;

      const nearby = findNearby(property);
      const label =
        nearby.length > 1
          ? `${nearby.length}`
          : `£${formatPrice(property.price || 0)}`;
      const isCluster = nearby.length > 1;
      const isFocused = focusId !== null && property.id === focusId;

      const el = document.createElement("div");
      el.className = isCluster
        ? `marker-card cursor-pointer ${
            isFocused
              ? "ring-4 ring-brand-yellow ring-offset-2 rounded-full"
              : ""
          }`
        : `marker-card bg-white shadow-[0_8px_20px_rgba(0,0,0,0.12)] border border-solid border-[rgba(0,0,0,0.08)] rounded-md overflow-hidden cursor-pointer transition-transform duration-200 hover:-translate-y-0.5 ${
            isFocused ? "ring-4 ring-brand-yellow ring-offset-2" : ""
          }`;
      el.innerHTML = `
        <div class="${
          isCluster
            ? "h-10 w-10 rounded-full bg-white text-text-obsidian font-teko text-base leading-tight flex items-center justify-center shadow-[0_6px_14px_rgba(0,0,0,0.18)] transition-colors duration-200 hover:bg-brand-yellow"
            : "px-3 py-2 bg-white text-text-obsidian font-teko text-base leading-tight text-center"
        }">${label}</div>
      `;
      const marker = new mapboxgl.Marker(el)
        .setLngLat(coords)
        .addTo(mapRef.current!);

      const handleHover = (e: MouseEvent) => {
        setHoveredSelection(nearby);
        if (mapRef.current) {
          const point = mapRef.current.project(coords as any);
          setPreviewPos({ x: point.x, y: point.y });
        }
      };

      el.addEventListener("mouseenter", handleHover);

      markersRef.current[String(property.id)] = marker;
    });
  }, [filtered, status, focusId]);

  useEffect(() => {
    if (mapRef.current && status === "succeeded") {
      loadDataOnMap();
    }
  }, [loadDataOnMap, status]);

  // After filters apply (or initial load), re-fit map to show all results.
  useEffect(() => {
    if (!mapRef.current || status !== "succeeded") return;
    fitMapToResults(filtered);
  }, [filtered, fitMapToResults, status]);

  // If focusId is provided, center map and open preview + scroll sidebar to that item.
  useEffect(() => {
    if (!mapRef.current || status !== "succeeded" || focusId === null) return;
    const focused = properties.find((p) => p.id === focusId);
    if (!focused) return;
    const coords = getLatLng(focused);
    if (!coords) return;
    mapRef.current.easeTo({
      center: coords,
      zoom: Math.max(defaultView.zoom, 15),
    });
    setHoveredSelection([focused]);
    const point = mapRef.current.project(coords as any);
    setPreviewPos({ x: point.x, y: point.y });
    // scroll sidebar to focused card
    setTimeout(() => {
      focusedCardRef.current?.scrollIntoView({ block: "center" });
    }, 0);
  }, [focusId, properties, status]);

  useEffect(() => {
    if (!mapRef.current) return;
    const clearOnMove = () => clearPreview();
    mapRef.current.on("movestart", clearOnMove);
    return () => {
      mapRef.current?.off("movestart", clearOnMove);
    };
  }, [clearPreview]);

  return (
    <>
    <div className="properties-container relative">
      {/* MAP LAYER */}
      <div className="map-wrapper relative w-full h-[calc(100vh-80px)] z-0">
        {(isLoading || status === "loading") && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
            <div className="animate-spin w-10 h-10 border-4 border-brand-yellow border-t-transparent rounded-full" />
          </div>
        )}
        {status === "failed" && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-20 text-red-600 font-manrope">
            {error || "Unable to load properties"}
          </div>
        )}
        <div
          id="map-view-map-container"
          ref={containerRef}
          className={`${styles["map-container"]} absolute inset-0`}
        ></div>
        {hoveredSelection.length > 0 && previewPos && (
          <div
            ref={previewRef}
            className="absolute z-40 w-72 bg-white shadow-2xl border border-solid border-[rgba(0,0,0,0.08)] rounded-md overflow-hidden"
            onMouseLeave={() => {
              setHoveredSelection([]);
              setPreviewPos(null);
            }}
          >
            {hoveredSelection.length === 1 ? (
              <div
                className="p-3 cursor-pointer"
                onClick={() =>
                  window.open(
                    `/services/rent/properties/${hoveredSelection[0].id}`,
                    "_blank"
                  )
                }
              >
                <SafeImage
                  src={getFirstImageUrl(hoveredSelection[0])}
                  alt={getDisplayAddress(hoveredSelection[0])}
                  className="w-full h-32 object-cover rounded-sm mb-2"
                />
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="font-teko text-lg leading-tight">
                    {getDisplayAddress(hoveredSelection[0])}
                  </div>
                  <div className="text-sm text-text-grey text-right min-w-[80px]">
                    £{formatPrice(hoveredSelection[0].price)}{" "}
                    {hoveredSelection[0].rent_frequency === "Monthly"
                      ? "PCM"
                      : hoveredSelection[0].rent_frequency}
                  </div>
                </div>
                <div className="text-xs text-[#666]">
                  {hoveredSelection[0].bedrooms ?? "-"} Bed •{" "}
                  {hoveredSelection[0].property_type}
                </div>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                {hoveredSelection.map((p: PropertyRaw) => (
                  <div
                    key={p.id}
                    className="flex gap-3 px-3 py-2 border-b border-b-solid border-b-[rgba(0,0,0,0.05)] hover:bg-[#fafafa] cursor-pointer items-center"
                    onClick={() =>
                      window.open(`/services/rent/properties/${p.id}`, "_blank")
                    }
                  >
                    <SafeImage
                      src={getFirstImageUrl(p)}
                      alt={getDisplayAddress(p)}
                      className="w-16 h-12 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-teko text-base leading-tight line-clamp-2">
                        {getDisplayAddress(p)}
                      </div>
                    </div>
                    <div className="text-xs text-[#555] text-right min-w-[80px]">
                      £{formatPrice(p.price)} {p.rent_frequency}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Markers are rendered by Mapbox GL JS */}
      </div>

      {/* LEFT: RESULTS LIST */}
      {/* Toggle attached to the RIGHT edge of the properties sidebar */}
      <button
        type="button"
        onClick={() => setLeftPanelOpen((v) => !v)}
        className={`absolute top-1/2 -translate-y-1/2 z-50 border border-solid border-[#ddd] shadow-md px-3 py-2 font-teko text-base uppercase bg-white ${
          leftPanelOpen ? "left-[445px]" : "left-0"
        }`}
        aria-label={leftPanelOpen ? "Hide properties" : "Show properties"}
      >
        {leftPanelOpen ? "Hide" : "Show"}
      </button>
      <aside
        className={`absolute top-0 left-0 w-[450px] max-w-[92vw] h-full bg-[var(--glass)] backdrop-blur-lg z-40 border-r border-r-solid border-r-[rgba(0,0,0,0.1)] shadow-xl flex flex-col transition-transform duration-300 ${
          leftPanelOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 border-b border-b-solid border-b-[#ddd]">
          <div className="font-teko text-2xl text-text-obsidian">
            Properties
          </div>
          <div className="text-xs text-text-grey">
            {status === "succeeded" ? `${filtered.length} results` : ""}
          </div>
        </div>

        <div className={`${styles["sb-list"]} flex-1 overflow-y-auto p-6`}>
          {status === "succeeded" && filtered.length === 0 && (
            <div className="text-text-grey bg-[#f9f9f9] border border-dashed border-[#ddd] p-4 rounded">
              No properties match the selected filters. Try widening your
              criteria.
            </div>
          )}
          {filtered.map((property) => {
            const imgUrl = getFirstImageUrl(property);
            const displayAddr = getDisplayAddress(property);

            let bedrooms: string | number = "";
            if ("bedrooms" in property) {
              bedrooms = (property as any).bedrooms || "";
            } else if ("num_bedrooms" in property) {
              bedrooms = (property as any).num_bedrooms || "";
            }

            return (
              <div
                key={property.id}
                ref={focusId === property.id ? focusedCardRef : undefined}
                className={`mini-card bg-white mb-6 cursor-pointer border border-solid transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_95%,95%_100%,0_100%)] hover:border-text-dark group ${
                  focusId === property.id
                    ? "border-brand-yellow shadow-[0_12px_30px_rgba(255,196,0,0.25)]"
                    : "border-gray-200"
                }`}
                onClick={() =>
                  window.open(
                    `/services/rent/properties/${property.id}`,
                    "_blank"
                  )
                }
              >
                <div className="overflow-hidden h-[180px] w-full">
                  <SafeImage
                    src={imgUrl}
                    alt={displayAddr}
                    className="mc-img h-[180px] w-full object-cover transition-transform duration-400 group-hover:scale-105"
                  />
                </div>
                <div className="mc-info p-4">
                  <div className="mc-price font-teko text-3xl text-brand-yellow leading-tight">
                    £{property.price?.toLocaleString() || ""}
                    {property.rent_frequency === "Monthly"
                      ? "PCM"
                      : property.rent_frequency === "Weekly"
                      ? "PW"
                      : ""}
                  </div>
                  <span className="mc-title font-bold text-base">
                    {displayAddr}
                  </span>
                  <span className="mc-addr text-xs text-[#666] mb-1 block">
                    {bedrooms} Bed • {property.property_type}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      {/* RIGHT: VIEW SWITCHER + FILTERS */}
      {!rightPanelOpen && (
        <button
          type="button"
          onClick={() => setRightPanelOpen(true)}
          className="absolute top-4 right-4 z-50 bg-brand-yellow border border-solid border-brand-yellow shadow-md px-4 py-2 font-teko text-base uppercase text-text-obsidian"
          aria-label="Show filters"
        >
          Show Filters
        </button>
      )}
      {rightPanelOpen && (
        <aside className="absolute top-4 right-4 w-[340px] max-w-[92vw] h-[calc(100vh-112px)] bg-white z-40 border border-solid border-[rgba(0,0,0,0.1)] shadow-xl overflow-hidden flex flex-col">
          <button
            type="button"
            onClick={() => setRightPanelOpen(false)}
            className="absolute top-3 right-3 z-50 font-teko text-3xl leading-none text-text-obsidian hover:text-brand-yellow"
            aria-label="Hide filters"
          >
            ×
          </button>
          <div className="p-6 border-b border-b-solid border-b-[#ddd]">
            <div className="font-teko text-xl text-text-obsidian mb-2">
              View
            </div>
            <ViewSwitcher fullWidth />
          </div>
          <div className="flex-1 overflow-hidden p-6 flex flex-col">
            <div className="font-teko text-xl text-text-obsidian mb-2 shrink-0">
              Filters
            </div>
            <div className="flex-1 min-h-0">
              <Filter
                value={appliedFilters}
                onApply={(next) => {
                  setAppliedFilters(next);
                  updateSearchParams(next);
                }}
                className="w-full flex flex-col h-full"
                variant="full"
                fullGridClassName="grid grid-cols-1 gap-4"
                actionsBarMode="bottom"
              />
            </div>
          </div>
        </aside>
      )}
    </div>
    <TenantGuides />
    <AreaGuideTeaser />
    <AboutAndContactForm />
    </>
  );
};

export default MapViewPage;
